/**
 * Keyboard shortcuts.
 *
 * DESIGN INTENT
 *   This product is meant to be driven from the keyboard. That only works if
 *   shortcuts are declared in one vocabulary, never fire while someone is
 *   typing their name into a field, and can be listed back to the user — which
 *   is why bindings carry a human description.
 *
 * DATA DEFINITION
 *   A Hotkey is { combo, when?, run, description?, group? }.
 *   Interpretation: `combo` is a normalised chord like "mod+k", "shift+?",
 *   "g then a" is NOT supported (sequences add ambiguity for little gain).
 *   `mod` means ⌘ on macOS and Ctrl elsewhere, so one declaration is correct
 *   on both. `when` lets a binding apply only in a context.
 */
import { useEffect, useRef } from 'react'

export interface Hotkey {
  combo: string
  run: (event: KeyboardEvent) => void
  description?: string
  group?: string
  /** Allow the binding to fire even while a text field has focus. */
  allowInInput?: boolean
  enabled?: boolean
}

/**
 * Is a modal dialog open?
 *
 * A shortcut that fires behind a dialog acts on something the person cannot
 * see — pressing "s" while confirming a delete would open the sort menu under
 * the overlay. Radix marks its open overlays and content with
 * data-state="open", and it also locks body scrolling, so either is a reliable
 * signal without this hook having to know which components exist.
 * modalIsOpen : -> Boolean
 */
function modalIsOpen(): boolean {
  if (typeof document === 'undefined') return false
  if (document.body.hasAttribute('data-scroll-locked')) return true
  return document.querySelector('[role="dialog"][data-state="open"],[role="alertdialog"][data-state="open"]') !== null
}

/** Is the event's target somewhere a person is typing? */
function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

/**
 * The chord an event represents, in the same vocabulary as `combo`.
 * comboOf : KeyboardEvent -> String
 */
export function comboOf(e: KeyboardEvent): string {
  const parts: string[] = []
  if (e.metaKey || e.ctrlKey) parts.push('mod')
  if (e.altKey) parts.push('alt')
  if (e.shiftKey) parts.push('shift')
  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key
  parts.push(key)
  return parts.join('+')
}

/**
 * Bind a set of shortcuts for as long as the component is mounted.
 *
 * Bindings are held in a ref so that re-rendering with new closures does not
 * detach and re-attach the listener on every keystroke.
 * useHotkeys : [Hotkey] -> void
 */
export function useHotkeys(hotkeys: Hotkey[]): void {
  const ref = useRef(hotkeys)
  ref.current = hotkeys

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      const combo = comboOf(e)
      const typing = isTyping(e.target)
      const blocked = modalIsOpen()
      for (const hotkey of ref.current) {
        if (hotkey.enabled === false) continue
        if (hotkey.combo !== combo) continue
        if (typing && !hotkey.allowInInput) continue
        // Escape must always work: it is how a dialog is dismissed.
        if (blocked && combo !== 'Escape') continue
        e.preventDefault()
        hotkey.run(e)
        return
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
}

/** Render a combo the way the platform writes it. displayCombo : String -> [String] */
export function displayCombo(combo: string): string[] {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  return combo.split('+').map(part => {
    if (part === 'mod') return isMac ? '⌘' : 'Ctrl'
    if (part === 'shift') return isMac ? '⇧' : 'Shift'
    if (part === 'alt') return isMac ? '⌥' : 'Alt'
    if (part === 'ArrowUp') return '↑'
    if (part === 'ArrowDown') return '↓'
    if (part === 'ArrowLeft') return '←'
    if (part === 'ArrowRight') return '→'
    if (part === 'Enter') return '↵'
    if (part === 'Escape') return 'Esc'
    if (part === 'Backspace') return '⌫'
    return part.length === 1 ? part.toUpperCase() : part
  })
}
